import React, { useEffect, useState } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton, Typography, Stack, Chip,
    CircularProgress, Alert, Table, TableContainer, TableHead, TableBody, TableRow, TableCell, Select, MenuItem, Switch,
    Tooltip, useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import PersonAddAlt1OutlinedIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import PersonRemoveOutlinedIcon from "@mui/icons-material/PersonRemoveOutlined";
import livechatApi from "./livechatApi";
import LeadSalesAddDialog from "./LeadSalesAddDialog";
import { LEAD_PRODUCT_OPTIONS, LEAD_PRODUCT_ALL } from "./livechatConstants";
import { PASTEL } from "./livechatTheme";

/**
 * DEV-1238 — "Kelola Sales Live Chat": admin mengatur spesialisasi produk tiap sales
 * (Parcel / Non Parcel / All Product), padanan Leads Monitoring autoleads WA. Percakapan baru yang
 * produknya (dipetakan dari halaman asal chat) sama dengan spesialisasi sales diprioritaskan ke
 * sales itu; "All Product" = ikut giliran biasa saja. Kolom Autoleads = override admin: OFF mematikan
 * & mengunci toggle sales (sales tidak bisa menyalakan sendiri), ON menyalakan & melepas kunci.
 * Kolom "Lead hari ini" = rekap lead yang diterima sejak 00:00 WIB (backend `?stats=today`):
 * kategori saling lepas — Selesai (ditutup tanpa label paid/unpaid), Paid, Unpaid — dan
 * Closing = (Selesai + Paid + Unpaid) / Diterima. Hanya dirender parent untuk
 * `Global.get().admin` — backend juga menolak non-admin (403).
 *
 * Whitelist autoleads: daftar ini HANYA berisi user yang dimasukkan admin (tombol "Tambah Sales",
 * cari user Lotus by nama/email/role). Agent lain yang membuka Live Chat (Corporate, mitra, CS)
 * tidak tampil dan tidak ikut pembagian lead. "Keluarkan" mematikan autoleads sales itu; percakapan
 * yang sudah ditugaskan tetap miliknya.
 */

const EMPTY_STATS = { received: 0, closed: 0, paid: 0, unpaid: 0, closingRate: 0 };

const STAT_COLUMNS = [
    { key: "received", label: "Diterima", hint: "Lead yang ditugaskan ke sales ini hari ini (otomatis maupun diambil manual)" },
    { key: "closed", label: "Selesai", hint: "Percakapan ditutup (tombol Selesai) tanpa label paid/unpaid" },
    { key: "paid", label: "Paid", hint: "Percakapan berlabel paid" },
    { key: "unpaid", label: "Unpaid", hint: "Percakapan berlabel unpaid" },
];

const headSx = { fontWeight: 700, whiteSpace: "nowrap" };
export default function LeadSalesDialog({ open, onClose }) {
    const isMobile = useMediaQuery("(max-width:600px)");
    const [agents, setAgents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [savingUserId, setSavingUserId] = useState(null);
    const [error, setError] = useState(null);
    const [reloadKey, setReloadKey] = useState(0);
    const [addOpen, setAddOpen] = useState(false);
    const [removeTarget, setRemoveTarget] = useState(null);
    const [removing, setRemoving] = useState(false);

    useEffect(() => {
        if (!open) return undefined;
        let cancelled = false;
        setLoading(true);
        setError(null);
        livechatApi
            .listAgents({ stats: "today" })
            .then(({ data }) => {
                if (!cancelled) setAgents(data.data || []);
            })
            .catch((err) => {
                if (!cancelled) setError(err?.response?.data?.message || "Gagal memuat daftar sales");
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [open, reloadKey]);

    const applyUpdate = (userId, updated) =>
        setAgents((prev) => prev.map((a) => (a.userId === userId ? { ...a, ...updated } : a)));

    const handleToggleAcceptingLeads = async (agent, acceptingLeads) => {
        setSavingUserId(agent.userId);
        setError(null);
        try {
            const { data } = await livechatApi.setAgentAcceptingLeads(agent.userId, acceptingLeads);
            applyUpdate(agent.userId, data.data || {});
        } catch (err) {
            console.error("Error in LeadSalesDialog.handleToggleAcceptingLeads:", err);
            setError(err?.response?.data?.message || "Gagal mengubah autoleads sales");
        } finally {
            setSavingUserId(null);
        }
    };

    const handleChangeLeadProduct = async (agent, leadProduct) => {
        if (leadProduct === (agent.leadProduct || LEAD_PRODUCT_ALL)) return;
        setSavingUserId(agent.userId);
        setError(null);
        try {
            const { data } = await livechatApi.setAgentLeadProduct(agent.userId, leadProduct);
            applyUpdate(agent.userId, data.data || {});
        } catch (err) {
            console.error("Error in LeadSalesDialog.handleChangeLeadProduct:", err);
            setError(err?.response?.data?.message || "Gagal menyimpan spesialisasi produk");
        } finally {
            setSavingUserId(null);
        }
    };

    const handleConfirmRemove = async () => {
        if (!removeTarget) return;
        setRemoving(true);
        setError(null);
        try {
            await livechatApi.setAgentMembership(removeTarget.userId, false);
            setAgents((prev) => prev.filter((a) => a.userId !== removeTarget.userId));
            setRemoveTarget(null);
        } catch (err) {
            console.error("Error in LeadSalesDialog.handleConfirmRemove:", err);
            setError(err?.response?.data?.message || "Gagal mengeluarkan sales");
            setRemoveTarget(null);
        } finally {
            setRemoving(false);
        }
    };

    const closeAddDialog = () => {
        setAddOpen(false);
        // muat ulang supaya sales yang baru ditambahkan tampil lengkap dgn rekap hari ini
        setReloadKey((k) => k + 1);
    };

    return (
        <>
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" fullScreen={isMobile}>
            <DialogTitle sx={{ pr: 11 }}>
                Kelola Sales Live Chat
                <Stack direction="row" sx={{ position: "absolute", right: 8, top: 8 }}>
                    <Tooltip title="Muat ulang data">
                        <span>
                            <IconButton onClick={() => setReloadKey((k) => k + 1)} disabled={loading} aria-label="Muat ulang">
                                <RefreshRoundedIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <IconButton onClick={onClose} aria-label="Tutup">
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent dividers>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "flex-start" }} sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                        Hanya sales di daftar ini yang ikut pembagian lead otomatis. Percakapan baru yang produknya
                        sesuai spesialisasi sales akan diutamakan ke sales tersebut; kalau tidak ada spesialis yang
                        online &amp; bersedia, percakapan dibagi bergiliran seperti biasa. Produk ditentukan otomatis
                        dari halaman asal chat pelanggan. Autoleads yang dimatikan di sini terkunci: sales tidak bisa
                        menyalakannya sendiri sampai dinyalakan kembali oleh admin.
                    </Typography>
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<PersonAddAlt1OutlinedIcon />}
                        onClick={() => setAddOpen(true)}
                        sx={{ textTransform: "none", whiteSpace: "nowrap", backgroundColor: PASTEL.primary, "&:hover": { backgroundColor: PASTEL.primary } }}
                    >
                        Tambah Sales
                    </Button>
                </Stack>

                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                {loading ? (
                    <Stack alignItems="center" sx={{ py: 4 }}>
                        <CircularProgress size={28} sx={{ color: PASTEL.primary }} />
                    </Stack>
                ) : agents.length === 0 ? (
                    <Typography color="text.secondary" sx={{ py: 3, textAlign: "center" }}>
                        Belum ada sales autoleads. Klik &quot;Tambah Sales&quot; untuk memilih user yang ikut pembagian lead.
                    </Typography>
                ) : (
                    <TableContainer sx={{ overflowX: "auto" }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={headSx} rowSpan={2}>Sales</TableCell>
                                    <TableCell sx={headSx} rowSpan={2}>Status</TableCell>
                                    <TableCell sx={headSx} rowSpan={2}>Autoleads</TableCell>
                                    <TableCell sx={{ ...headSx, minWidth: 160 }} rowSpan={2}>Spesialisasi Produk</TableCell>
                                    <TableCell sx={{ ...headSx, borderBottom: `1px solid ${PASTEL.border}` }} colSpan={STAT_COLUMNS.length + 1} align="center">
                                        Lead hari ini
                                    </TableCell>
                                    <TableCell sx={headSx} rowSpan={2} align="right">Aksi</TableCell>
                                </TableRow>
                                <TableRow>
                                    {STAT_COLUMNS.map((c) => (
                                        <TableCell key={c.key} sx={headSx} align="right">
                                            <Tooltip title={c.hint}><span>{c.label}</span></Tooltip>
                                        </TableCell>
                                    ))}
                                    <TableCell sx={headSx} align="right">
                                        <Tooltip title="(Selesai + Paid + Unpaid) ÷ Diterima"><span>Closing</span></Tooltip>
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {agents.map((agent) => (
                                    <TableRow key={agent.userId}>
                                        <TableCell>{agent.userName || `Sales #${agent.userId}`}</TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                label={agent.online ? "Online" : "Offline"}
                                                sx={agent.online ? { backgroundColor: "rgba(46,125,50,0.1)", color: "#2e7d32" } : undefined}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                                <Switch
                                                    size="small"
                                                    checked={!!agent.acceptingLeads}
                                                    disabled={savingUserId === agent.userId}
                                                    onChange={(e) => handleToggleAcceptingLeads(agent, e.target.checked)}
                                                    inputProps={{ "aria-label": `Autoleads ${agent.userName || agent.userId}` }}
                                                    sx={{
                                                        "& .MuiSwitch-switchBase.Mui-checked": { color: PASTEL.primary },
                                                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: PASTEL.primary },
                                                    }}
                                                />
                                                {agent.lockedByAdmin && (
                                                    <Tooltip title={`Dimatikan oleh ${agent.lockedByName || "admin"} — sales tidak bisa menyalakan sendiri`}>
                                                        <Chip size="small" label="Dikunci admin" sx={{ height: 20, fontSize: 11, backgroundColor: "rgba(198,40,40,0.1)", color: "#c62828" }} />
                                                    </Tooltip>
                                                )}
                                            </Stack>
                                        </TableCell>
                                        <TableCell>
                                            <Select
                                                size="small"
                                                fullWidth
                                                value={agent.leadProduct || LEAD_PRODUCT_ALL}
                                                disabled={savingUserId === agent.userId}
                                                onChange={(e) => handleChangeLeadProduct(agent, e.target.value)}
                                                inputProps={{ "aria-label": `Spesialisasi ${agent.userName || agent.userId}` }}
                                            >
                                                {LEAD_PRODUCT_OPTIONS.map((option) => (
                                                    <MenuItem key={option} value={option}>{option}</MenuItem>
                                                ))}
                                            </Select>
                                        </TableCell>
                                        {STAT_COLUMNS.map((c) => (
                                            <TableCell key={c.key} align="right">{(agent.todayStats || EMPTY_STATS)[c.key]}</TableCell>
                                        ))}
                                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                                            {(agent.todayStats || EMPTY_STATS).closingRate}%
                                        </TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="Keluarkan dari autoleads">
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => setRemoveTarget(agent)}
                                                        disabled={savingUserId === agent.userId}
                                                        aria-label={`Keluarkan ${agent.userName || agent.userId}`}
                                                    >
                                                        <PersonRemoveOutlinedIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 1.5 }}>
                <Button onClick={onClose} sx={{ textTransform: "none" }}>Tutup</Button>
            </DialogActions>
        </Dialog>

        <LeadSalesAddDialog open={addOpen} onClose={closeAddDialog} />

        <Dialog open={!!removeTarget} onClose={() => !removing && setRemoveTarget(null)} maxWidth="xs" fullWidth>
            <DialogTitle>Keluarkan dari autoleads?</DialogTitle>
            <DialogContent>
                <Typography variant="body2">
                    <b>{removeTarget?.userName || `Sales #${removeTarget?.userId}`}</b> tidak lagi menerima lead baru
                    dan tidak bisa menyalakan &quot;Autoleads&quot; sampai ditambahkan kembali.
                </Typography>
                {removeTarget?.activeSessions > 0 && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        Masih ada {removeTarget.activeSessions} percakapan aktif milik sales ini. Percakapan tersebut tetap
                        miliknya dan masih bisa dilihat Leader/SPV.
                    </Alert>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setRemoveTarget(null)} disabled={removing} sx={{ textTransform: "none" }}>Batal</Button>
                <Button onClick={handleConfirmRemove} disabled={removing} color="error" variant="contained" sx={{ textTransform: "none" }}>
                    Keluarkan
                </Button>
            </DialogActions>
        </Dialog>
        </>
    );
}
